import React, { useState, useEffect } from 'react';
import { Wallet, RefreshCw, Upload, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { api } from '@/lib/api.ts';

const RefundModal = ({ isOpen, onClose, orderId, email, onSubmit, initialPaymentData }) => {
    const [reason, setReason] = useState('defective');
    const [refundType, setRefundType] = useState('money'); 
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState({ evidence1: null, evidence2: null });
    
    // Datos de pago
    const [paymentData, setPaymentData] = useState({ banco: '', identificacion: '', telefono: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && initialPaymentData && refundType === 'money') {
            setPaymentData(prev => ({
                ...prev,
                banco: initialPaymentData.banco || prev.banco,
                identificacion: initialPaymentData.identificacion || prev.identificacion,
                telefono: initialPaymentData.telefono || prev.telefono
            }));
        }
    }, [isOpen, initialPaymentData, refundType]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description) return toast.error('Describe el problema');
        if (!files.evidence1) return toast.error('Sube al menos una foto de evidencia');
        
        if (refundType === 'money') {
            if (!paymentData.banco || !paymentData.identificacion || !paymentData.telefono) {
                return toast.error('Completa tus datos bancarios para recibir el dinero');
            }
        }

        setIsSubmitting(true);
        try {
            const uploadImage = async (file) => {
                const compressedFile = await imageCompression(file, {
                    maxSizeMB: 0.5,
                    maxWidthOrHeight: 2000,
                    useWebWorker: true,
                    quality: 0.9,
                    fileType: 'image/jpeg'
                });

                const finalFile = new File([compressedFile], file.name, { type: compressedFile.type });
                const contentType = finalFile.type || 'application/octet-stream';
                
                const { data } = await api.post(`/api/orders/${orderId}/refund-proof-url`, {
                    fileName: finalFile.name,
                    contentType: contentType
                });
                
                const response = await fetch(data.uploadURL, { 
                    method: 'PUT', 
                    body: finalFile, 
                    headers: { 'Content-Type': contentType } 
                });

                if (!response.ok) throw new Error('Error subiendo imagen a la nube');
                return data.filePath;
            };

            const path1 = await uploadImage(files.evidence1);
            const path2 = files.evidence2 ? await uploadImage(files.evidence2) : null;

            await onSubmit({
                reason,
                description,
                refund_type: refundType,
                customer_payment_data: refundType === 'money' ? paymentData : '',
                evidence_1_path: path1,
                evidence_2_path: path2,
                email
            });
            onClose();
        } catch (error) {
            console.error('❌ Error completo:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Error al enviar solicitud';
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in overflow-y-auto">
            <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl my-auto">
                <h3 className="text-lg font-bold text-white mb-1">Solicitar Devolución</h3>
                <p className="text-xs text-white/50 mb-4">Cuéntanos qué pasó con tu producto.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <label className={`cursor-pointer rounded-xl border p-3 flex flex-col items-center gap-2 transition-all ${refundType === 'money' ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-black border-white/10 text-white/40 hover:bg-white/5'}`}>
                            <input type="radio" name="rtype" className="hidden" checked={refundType === 'money'} onChange={() => setRefundType('money')} />
                            <Wallet className="h-5 w-5" />
                            <span className="text-[10px] font-bold uppercase tracking-widest">Reembolso</span>
                        </label>
                        <label className={`cursor-pointer rounded-xl border p-3 flex flex-col items-center gap-2 transition-all ${refundType === 'replacement' ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-400' : 'bg-black border-white/10 text-white/40 hover:bg-white/5'}`}>
                            <input type="radio" name="rtype" className="hidden" checked={refundType === 'replacement'} onChange={() => setRefundType('replacement')} />
                            <RefreshCw className="h-5 w-5" />
                            <span className="text-[10px] font-bold uppercase tracking-widest">Cambio</span>
                        </label>
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">Motivo</label>
                        <select value={reason} onChange={e => setReason(e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-red-500">
                            <option value="defective">Producto defectuoso / Dañado</option>
                            <option value="wrong_item">Producto incorrecto</option>
                            <option value="did_not_like">Me arrepentí / No me gustó</option>
                            <option value="size_issue">Talla incorrecta</option>
                            <option value="other">Otro</option>
                        </select>
                    </div>
                    
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">Descripción</label>
                        <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-white text-sm h-20 resize-none outline-none focus:border-red-500" placeholder="Explica el detalle..." />
                    </div>

                    {refundType === 'money' && (
                        <div className="bg-emerald-900/10 border border-emerald-500/20 rounded-xl p-3 space-y-2 animate-in slide-in-from-top-2">
                            <div className="flex justify-between items-center mb-1">
                                <p className="text-[10px] font-bold text-emerald-400 uppercase">Datos para recibir tu dinero</p>
                                {initialPaymentData?.banco && <span className="text-[9px] text-emerald-500/50 uppercase tracking-wider">Autocompletado</span>}
                            </div>
                            
                            <select 
                                value={paymentData.banco} 
                                onChange={e => setPaymentData({...paymentData, banco: e.target.value})} 
                                className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:border-emerald-500 outline-none cursor-pointer"
                            >
                                <option value="">Selecciona tu banco</option>
                                <option value="Banco de Venezuela (0102)">Banco de Venezuela (0102)</option>
                                <option value="BBVA Provincial (0108)">BBVA Provincial (0108)</option>
                                <option value="Banesco (0134)">Banesco (0134)</option>
                                <option value="Mercantil (0105)">Mercantil (0105)</option>
                                <option value="Banco Nacional de Crédito (0191)">Banco Nacional de Crédito - BNC (0191)</option>
                                <option value="Bancaribe (0114)">Bancaribe (0114)</option>
                                <option value="Bancamiga (0172)">Bancamiga (0172)</option>
                                <option value="Banplus (0174)">Banplus (0174)</option>
                                <option value="Banco Venezolano de Crédito (0104)">Banco Venezolano de Crédito - BVC (0104)</option>
                                <option value="Exterior (0115)">Exterior (0115)</option>
                                <option value="100% Banco (0156)">100% Banco (0156)</option>
                                <option value="Del Sur (0157)">Del Sur (0157)</option>
                                <option value="Banco Activo (0171)">Banco Activo (0171)</option>
                                <option value="Sofitasa (0137)">Sofitasa (0137)</option>
                                <option value="Banco Plaza (0138)">Banco Plaza (0138)</option>
                                <option value="Banco del Tesoro (0163)">Banco del Tesoro (0163)</option>
                                <option value="Banco Agrícola de Venezuela (0166)">Banco Agrícola de Venezuela (0166)</option>
                                <option value="BANFANB (0177)">BANFANB (0177)</option>
                                <option value="Bicentenario (0175)">Bicentenario (0175)</option>
                                <option value="Bangente (0146)">Bangente (0146)</option>
                                <option value="Bancrecer (0168)">Bancrecer (0168)</option>
                                <option value="Mi Banco (0169)">Mi Banco (0169)</option>
                            </select>
                            
                            <div className="grid grid-cols-2 gap-2">
                                <input placeholder="C.I / RIF" value={paymentData.identificacion} onChange={e => setPaymentData({...paymentData, identificacion: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:border-emerald-500 outline-none" />
                                <input placeholder="Teléfono Pago Móvil" value={paymentData.telefono} onChange={e => setPaymentData({...paymentData, telefono: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:border-emerald-500 outline-none" />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">Evidencia (Fotos)</label>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="h-16 rounded-lg border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 transition-colors">
                                {files.evidence1 ? <span className="text-[10px] text-green-400 truncate w-20 text-center">{files.evidence1.name}</span> : <ImageIcon className="h-4 w-4 text-white/30"/>}
                                <span className="text-[8px] text-white/30 mt-1">Foto 1 *</span>
                                <input type="file" className="hidden" accept="image/*" onChange={e => setFiles({...files, evidence1: e.target.files[0]})} />
                            </label>
                            <label className="h-16 rounded-lg border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 transition-colors">
                                {files.evidence2 ? <span className="text-[10px] text-green-400 truncate w-20 text-center">{files.evidence2.name}</span> : <Upload className="h-4 w-4 text-white/30"/>}
                                <span className="text-[8px] text-white/30 mt-1">Foto 2</span>
                                <input type="file" className="hidden" accept="image/*" onChange={e => setFiles({...files, evidence2: e.target.files[0]})} />
                            </label>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-xs font-bold uppercase tracking-widest text-white hover:bg-white/5 transition-colors">Cancelar</button>
                        <button type="submit" disabled={isSubmitting} className="flex-1 py-2 rounded-lg bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-gray-200 disabled:opacity-50 transition-colors">
                            {isSubmitting ? 'Enviando...' : 'Confirmar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RefundModal;